// NodePeopleField.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Utilities
import {
  getPersonCoverUrl,
  getPersonKey as getIdentityPersonKey,
  getPersonName as getIdentityPersonName,
} from 'utilities/PersonIdentity';

// Hooks
import useNodesByKeysQuery from 'hooks/useNodesByKeysQuery';

// CSS
import './NodePeopleField.css';

// Components
import MemberPickerModal from 'components/memberpicker/MemberPickerModal';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import ProfileStack from 'components/profilestack/ProfileStack';

const getPersonKey = (person) => String(person?.userKey || person?.key || '').trim();

const getPersonName = (person) => (
  getIdentityPersonName(person)
  || getPersonKey(person)
);

const normalizePeople = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const map = new Map();

  list.forEach((entry) => {
    if (!entry) return;
    const key = getPersonKey(entry);
    if (!key) return;

    map.set(key, {
      key,
      userKey: key,
      name: getPersonName(entry),
      title: entry?.title || getPersonName(entry),
      email: entry?.email || entry?.data_profile?.email || '',
      coverUrl: getPersonCoverUrl(entry) || '',
      coverStoragePath: String(
        entry?.coverStoragePath
        || entry?.storagePath
        || entry?.data_profile?.coverStoragePath
        || '',
      ).trim(),
      data_profile: {
        name: getPersonName(entry),
        email: entry?.email || entry?.data_profile?.email || '',
        coverUrl: getPersonCoverUrl(entry) || '',
      },
    });
  });

  return Array.from(map.values());
};

/**
 * Prefer live USER / current-session cover data over the stored people snapshot.
 *
 * @param {object} person
 * @param {object|null|undefined} liveUser
 * @param {object|null|undefined} sessionUser
 * @returns {object}
 */
const enrichPersonForDisplay = (person, liveUser, sessionUser) => {
  const key = getPersonKey(person);
  const sessionMatch = getIdentityPersonKey(sessionUser) === key ? sessionUser : null;
  const sources = [liveUser, sessionMatch, person].filter(Boolean);

  const coverUrl = sources.map((source) => getPersonCoverUrl(source)).find(Boolean) || '';
  const coverStoragePath = sources
    .map((source) => String(
      source?.coverStoragePath
      || source?.storagePath
      || source?.data_profile?.coverStoragePath
      || '',
    ).trim())
    .find(Boolean) || '';

  const name = getIdentityPersonName(liveUser)
    || getIdentityPersonName(sessionMatch)
    || getPersonName(person);

  return {
    ...person,
    key,
    userKey: key,
    name,
    title: name,
    coverUrl,
    coverStoragePath,
    data_profile: {
      ...(person?.data_profile || {}),
      name,
      coverUrl,
      coverStoragePath: coverStoragePath || undefined,
    },
  };
};

/**
 * NodePeopleField
 *
 * People chips with an optional member picker. Supports readOnly / previewMode
 * (visitor Peek): display only — no hover affordance, pointer, or picker.
 * When `field.showAsProfileStack` is true, renders an overlapping ProfileStack.
 *
 * @param {object} node - Node object (for context, type label, etc.).
 * @param {object} [field] - Schema field definition.
 * @param {string} value - Current field value.
 * @param {string} fieldName - Name of the field (for popup context).
 * @param {string} fieldLabel - Label for the field.
 * @param {Function} onChange - Callback when new value is set.
 * @param {boolean} [readOnly=false] - Display-only; no picker.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} - NodePeopleField.
 */
const NodePeopleField = ({
  node,
  field,
  fieldName,
  fieldLabel,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { user } = useUser();
  const { activeBase } = useBase();

  // ----- STATE ----- //

  const isReadOnly = Boolean(readOnly || previewMode);
  const showAsProfileStack = field?.showAsProfileStack === true;
  const [modalOpen, setModalOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  // ----- MEMOS ----- //

  const people = useMemo(() => normalizePeople(value), [value]);

  const personKeys = useMemo(
    () => people.map((person) => person.userKey).filter(Boolean),
    [people],
  );

  // ----- HOOKS ----- //

  // Hydrate live USER docs so photos stay current even if the stored people
  // snapshot was saved without (or with a stale) coverUrl.
  const { nodesByKey: liveUsersByKey } = useNodesByKeysQuery(personKeys, {
    enabled: personKeys.length > 0,
  });

  const displayPeople = useMemo(() => (
    people.map((person) => enrichPersonForDisplay(
      person,
      liveUsersByKey.get(person.userKey) || null,
      user,
    ))
  ), [liveUsersByKey, people, user]);

  const stackProfiles = useMemo(() => (
    displayPeople.map((person) => ({
      key: person.userKey,
      userKey: person.userKey,
      baseKey: node?.baseKey || activeBase?.key || null,
      name: getPersonName(person),
      title: getPersonName(person),
      coverUrl: person.coverUrl || null,
      coverStoragePath: person.coverStoragePath || null,
      storagePath: person.coverStoragePath || null,
      data_profile: person.data_profile,
    }))
  ), [activeBase?.key, displayPeople, node?.baseKey]);

  // ----- HANDLERS ----- //

  const handleOpen = () => {
    if (isReadOnly) return;
    setModalOpen(true);
  };

  const handleKeyDown = (event) => {
    if (isReadOnly) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setModalOpen(true);
    }
  };

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-people-field-container">

        <div
          className={`node-people-field-input${isReadOnly ? ' is-readonly' : ''}${showAsProfileStack ? ' is-stack' : ''}`}
          style={{
            backgroundColor: (!isReadOnly && hovered) ? theme.backgroundMuted : 'transparent',
            cursor: isReadOnly ? 'default' : undefined,
          }}
          role={isReadOnly ? undefined : 'button'}
          tabIndex={isReadOnly ? undefined : 0}
          onClick={isReadOnly ? undefined : handleOpen}
          onKeyDown={isReadOnly ? undefined : handleKeyDown}
          onMouseEnter={isReadOnly ? undefined : () => setHovered(true)}
          onMouseLeave={isReadOnly ? undefined : () => setHovered(false)}
        >

          {displayPeople.length === 0 && (
            <div className="node-people-field-empty">
              {isReadOnly ? '' : 'Select people...'}
            </div>
          )}

          {displayPeople.length > 0 && showAsProfileStack && (
            <div
              className={`node-people-field-stack${isReadOnly ? '' : ' is-picker'}`}
              title={stackProfiles.map((profile) => profile.name).filter(Boolean).join(', ')}
            >
              <ProfileStack
                profiles={stackProfiles}
                size={28}
                maxDisplay={5}
                overlap={4}
                allowNavigation={false}
              />
            </div>
          )}

          {displayPeople.length > 0 && !showAsProfileStack && (
            <div className="node-people-field-list">
              {displayPeople.map((person) => (
                <div className="node-people-field-person" key={person.userKey}>
                  <ProfilePhoto
                    node={{
                      key: person.userKey,
                      userKey: person.userKey,
                      baseKey: node?.baseKey || activeBase?.key || null,
                      name: getPersonName(person),
                      title: getPersonName(person),
                      coverUrl: person.coverUrl || null,
                      coverStoragePath: person.coverStoragePath || null,
                      storagePath: person.coverStoragePath || null,
                    }}
                    size={24}
                    borderWidth={1}
                  />

                  <div className="node-people-field-name">
                    {getPersonName(person)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {!isReadOnly ? (
        <MemberPickerModal
          node={node}
          fieldName={fieldName}
          label={fieldLabel}
          value={people}
          isOpen={modalOpen}
          setOpen={setModalOpen}
          onChange={(nextPeople) => onChange?.(nextPeople)}
        />
      ) : null}
    </>
  );
};

NodePeopleField.displayName = 'NodePeopleField';

export default NodePeopleField;
